import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, ShieldCheck, Loader2, WandSparkles } from "lucide-react";

/**
 * Shown right after a new listing is created. Runs the AI normalization,
 * shows the owner a cleaned version, and lets them choose:
 *  - Use this   → adopt the cleaned title + description (and SEO meta)
 *  - Keep mine  → keep their own words visible, still apply invisible SEO meta
 *  - Edit       → adopt the cleaned text, then open the editor to tweak
 * Never blocks: if the AI errors, we quietly resolve and move on.
 */
export default function NormalizeReviewModal({ open, propertyId, onResolved }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !propertyId) return;
    let cancelled = false;
    setLoading(true);
    setDraft(null);
    api.post(`/properties/${propertyId}/normalize`)
      .then(({ data }) => { if (!cancelled) setDraft(data); })
      .catch(() => { if (!cancelled) onResolved?.(); }) // fail-safe: skip silently
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyId]);

  const apply = async (useVisible, thenEdit = false) => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/normalize/apply`, { use_visible: useVisible });
      if (useVisible) toast.success("Great — your listing is polished for search");
      if (thenEdit) { onResolved?.(); nav(`/edit-property/${propertyId}`); return; }
    } catch (_) { /* non-blocking */ }
    finally { setBusy(false); }
    onResolved?.();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onResolved?.(); }}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="normalize-review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl brand-forest flex items-center gap-2">
            <WandSparkles size={20} className="text-[#C9A24A]" />
            We tidied up your listing for better search visibility
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-14 flex flex-col items-center justify-center text-gray-500" data-testid="normalize-loading">
            <Loader2 size={28} className="animate-spin mb-3 brand-forest" />
            <p className="text-sm">Polishing your title & description…</p>
          </div>
        ) : draft ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Here's a cleaner, more searchable version. Your original is always saved — pick what you prefer.
            </p>

            <div className="rounded-xl border border-brand-subtle overflow-hidden">
              <div className="px-4 py-2 bg-[#FFFBEE] text-[11px] font-bold tracking-widest uppercase text-[#C9A24A] flex items-center gap-1">
                <Sparkles size={12} /> Suggested title
              </div>
              <p className="px-4 py-3 font-semibold text-gray-900" data-testid="normalize-title-clean">
                {draft.title_clean || "—"}
              </p>
            </div>

            <div className="rounded-xl border border-brand-subtle overflow-hidden">
              <div className="px-4 py-2 bg-[#FFFBEE] text-[11px] font-bold tracking-widest uppercase text-[#C9A24A] flex items-center gap-1">
                <Sparkles size={12} /> Suggested description
              </div>
              <p className="px-4 py-3 text-sm text-gray-700 whitespace-pre-line max-h-40 overflow-y-auto" data-testid="normalize-desc-clean">
                {draft.description_clean || "—"}
              </p>
            </div>

            {Array.isArray(draft.tags) && draft.tags.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="normalize-tags">
                {draft.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-[#F3EEE4] text-[#7A1E2A] text-xs font-medium">
                    {t}
                  </span>
                ))}
              </div>
            )}

            {Array.isArray(draft.contacts_removed) && draft.contacts_removed.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="normalize-contacts-removed">
                <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span>
                  We removed contact details ({draft.contacts_removed.join(", ")}) from your text.
                  Renters unlock your phone through To-Let so you stay in control.
                </span>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => apply(false)}
                data-testid="normalize-keep-mine"
                className="rounded-full text-gray-600"
              >
                Keep mine
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => apply(true, true)}
                data-testid="normalize-edit"
                className="rounded-full border-brand-subtle"
              >
                Edit
              </Button>
              <Button
                disabled={busy}
                onClick={() => apply(true)}
                data-testid="normalize-use-this"
                className="tlr-gold-btn rounded-full px-6"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Use this"}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
